import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["< ₹5L", "₹5L – ₹15L", "₹15L – ₹50L", "₹50L – ₹1Cr", "₹1Cr+"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.company) {
      toast.error("Name, email, phone and company are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Got it! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", phone: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="text-zinc-100">
      <section className="relative pt-36 md:pt-44 pb-12 border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/30 via-zinc-950 to-zinc-950" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="font-mono-accent text-[11px] text-amber-400 mb-5">Contact</div>
          <h1 className="font-display text-5xl md:text-7xl font-black leading-[0.95] tracking-tighter max-w-4xl">
            Plan your <span className="gradient-text italic">campaign.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            Tell us about your brand, audience, and goal. We'll come back with a
            recommended media plan, locations, and pricing within 24 hours.
          </p>
        </div>
      </section>

      <section className="bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 rounded-3xl border border-white/5 bg-zinc-900/50 p-8 md:p-10"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Your Name" required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-input-name"
                    className="form-input"
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-input-email"
                    className="form-input"
                    placeholder="jane@brand.com"
                  />
                </Field>
                <Field label="Company" required>
                  <input
                    type="text"
                    required
                    value={form.company}
                    onChange={update("company")}
                    data-testid="contact-input-company"
                    className="form-input"
                    placeholder="Brand Inc."
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-input-phone"
                    className="form-input"
                    placeholder="+91 ..."
                  />
                </Field>
                <Field label="Budget Range">
                  <select
                    value={form.budget}
                    onChange={update("budget")}
                    data-testid="contact-select-budget"
                    className="form-input"
                  >
                    <option value="">Select range</option>
                    {BUDGETS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Tell us more" className="mt-5">
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  className="form-input resize-none"
                  placeholder="Audience, cities, timeline, references..."
                />
              </Field>
              <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-xs text-zinc-500">
                  By submitting, you agree to be contacted by Horizn Ad Media.
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-zinc-950 px-7 py-3.5 text-sm font-bold hover:bg-amber-300 transition-all disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Plan my campaign <ArrowUpRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>

              <style>{`
                .form-input {
                  width: 100%;
                  background: rgba(255,255,255,0.03);
                  border: 1px solid rgba(255,255,255,0.08);
                  border-radius: 12px;
                  padding: 14px 16px;
                  color: #fafafa;
                  font-family: 'Manrope', sans-serif;
                  font-size: 14px;
                  transition: border-color .2s, background .2s;
                  outline: none;
                }
                .form-input::placeholder { color: #71717a; }
                .form-input:focus {
                  border-color: rgba(96,165,250,0.5);
                  background: rgba(255,255,255,0.05);
                }
                .form-input option { background: #18181b; color: #fafafa; }
              `}</style>
            </motion.form>

            <motion.aside
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5 space-y-5"
            >
              <ContactCard
                icon={Mail}
                label="Email"
                value="horiznadmediapvtltd@gmail.com"
                href="mailto:horiznadmediapvtltd@gmail.com"
                accent="text-blue-400"
                testid="contact-card-email"
              />
              <ContactCard
                icon={Phone}
                label="Phone"
                value="+91 94122 22077"
                href="tel:+919412222077"
                accent="text-purple-400"
                testid="contact-card-phone-1"
              />
              <ContactCard
                icon={Phone}
                label="Phone"
                value="+91 96677 22027"
                href="tel:+919667722027"
                accent="text-purple-400"
                testid="contact-card-phone-2"
              />
              <ContactCard
                icon={MessageCircle}
                label="WhatsApp"
                value="Chat with us"
                href="https://wa.me/9412222077"
                accent="text-green-400"
                testid="contact-card-whatsapp"
              />
              <ContactCard
                icon={MapPin}
                label="Office"
                value="F-2, Savitri Market, Sector 18, Noida, Uttar Pradesh 201301"
                accent="text-amber-400"
                testid="contact-card-location"
              />

              <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-zinc-900/50">
                <iframe
                  title="Horizn Ad Media Office Map"
                  src="https://www.google.com/maps?q=F-2,+Savitri+Market,+Sector+18,+Noida,+Uttar+Pradesh+201301&output=embed"
                  className="w-full h-[280px] grayscale contrast-125 opacity-80"
                  loading="lazy"
                  data-testid="contact-map-iframe"
                />
              </div>
            </motion.aside>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">
        {label} {required && <span className="text-amber-400">*</span>}
      </div>
      {children}
    </label>
  );
}

function ContactCard({ icon: Icon, label, value, href, accent, testid }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      target={href && href.startsWith("http") ? "_blank" : undefined}
      rel={href && href.startsWith("http") ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className="block rounded-2xl border border-white/5 bg-zinc-900/50 p-6 hover:border-white/15 transition-colors"
    >
      <div className="flex items-start gap-4">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 ${accent}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className="font-mono-accent text-[10px] text-zinc-500">{label}</div>
          <div className="mt-1 font-display text-lg font-semibold text-white">
            {value}
          </div>
        </div>
      </div>
    </Wrapper>
  );
}
